import React from 'react'
import { useTranslation } from 'react-i18next'

import MuiLink from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

import Alert from '../components/alert/alert'
import { login, setPageTitle } from '../utils/service'

const LoginView = (): JSX.Element => {
  const { t } = useTranslation()
  const [username, setUsername] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [isLoggingIn, setIsLoggingIn] = React.useState<boolean>(false)
  const [loginError, setLoginError] = React.useState<string>('')
  const [showPassword, setShowPassword] = React.useState<boolean>(false)

  const onLogin = (): void => {
    setIsLoggingIn(true)
    setLoginError('')
    login(
      username,
      password,
      () => {
        setIsLoggingIn(false)
        window.location.href = '/' // hard navigate to load data
      },
      (statusCode: number | undefined, invalidGrant: boolean, noResponse: boolean, baseUrl: string | undefined) => {
        setIsLoggingIn(false)
        if ((statusCode === 400 || statusCode === 401) && invalidGrant) {
          setLoginError(t('loginview.unauthorized'))
        } else if (statusCode) {
          setLoginError(t('loginview.unexpectedresponse', { statusCode: statusCode }))
        } else if (noResponse && baseUrl) {
          setLoginError(t('loginview.couldnotcontact', { baseUrl: baseUrl }))
        } else {
          setLoginError(t('loginview.genericerror'))
        }
      }
    )
  }

  // update page title
  React.useEffect(() => {
    setPageTitle(t('loginview.login'))
  }, [t])

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    if (!!username && !!password) {
      onLogin()
    }
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  return (
    <Container component="main" maxWidth="xs">
      <form onSubmit={onFormSubmit}>
        <Stack spacing={3}>
          <Typography variant="h4" component="h1">
            {t('loginview.login')}
          </Typography>
          <TextField
            fullWidth
            label={t('loginview.username')}
            variant="outlined"
            value={username}
            inputProps={{
              autoCapitalize: 'off',
              autoCorrect: 'off',
            }}
            onChange={(event) => {
              setUsername(event.target.value)
            }}
          />
          <TextField
            fullWidth
            label={t('loginview.password')}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            InputProps={{
              autoCapitalize: 'off',
              autoCorrect: 'off',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <LoadingButton type="submit" variant="contained" loading={isLoggingIn}>
              {t('loginview.login')}
            </LoadingButton>
            <MuiLink href={`${process.env.REACT_APP_DJANGO_URL}/accounts/password_reset/`}>
              {t('loginview.forgotpassword')}
            </MuiLink>
          </Stack>
          {loginError && <Alert severity="error">{loginError}</Alert>}
        </Stack>
      </form>
    </Container>
  )
}

export default LoginView
